// Typsicherer API-Client, generiert aus contracts/openapi.yaml (`pnpm gen:api`).
import createClient, { type Middleware } from 'openapi-fetch'
import { getAccessToken, notifySessionEnded } from '@/auth/oidc'
import type { components, paths } from './schema'

export type Problem = components['schemas']['Problem']
export type Schemas = components['schemas']

/** Einheitlicher Fehler für Queries/Mutationen; `problem` ist der RFC-9457-Body, falls vorhanden. */
export class ApiError extends Error {
  readonly status: number
  readonly problem?: Problem

  constructor(status: number, problem?: Problem) {
    super(problem?.title ?? `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }
}

export const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = await getAccessToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
  onResponse({ response }) {
    // 401 trotz erneuertem Token: Sitzung ist nicht mehr gültig.
    if (response.status === 401) notifySessionEnded('expired')
    return response
  },
}

/** Schreibende Aufrufe tragen immer einen Idempotency-Key (Wiederholung desselben Klicks bleibt harmlos). */
export const idempotencyMiddleware: Middleware = {
  onRequest({ request }) {
    if (
      request.method !== 'GET' &&
      request.method !== 'HEAD' &&
      !request.headers.has('Idempotency-Key')
    ) {
      request.headers.set('Idempotency-Key', crypto.randomUUID())
    }
    return request
  },
}

// fetch erst beim Aufruf auflösen: MSW (Tests, Dev-Mocks) ersetzt globalThis.fetch nach dem Import dieses Moduls
export const lazyFetch: typeof fetch = (input, init) => globalThis.fetch(input, init)

export const api = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_BASE_URL || window.location.origin,
  fetch: lazyFetch,
})
api.use(authMiddleware)
api.use(idempotencyMiddleware)

/** Wertet ein openapi-fetch-Ergebnis aus: Daten oder ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined) return result.data
  throw new ApiError(result.response.status, asProblem(result.error))
}

function asProblem(error: unknown): Problem | undefined {
  return typeof error === 'object' && error !== null && 'title' in error
    ? (error as Problem)
    : undefined
}

/** Backend-Endpunkte, die es noch nicht gibt (404/501), werden zu `null` statt zum Fehler. */
export function isNotAvailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 501)
}
